/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * TablePermissionsListPage
 *
 * Page responsible for listing per-table permissions for a specific
 * application user, with server-side pagination, progress bar, and
 * alert handling.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useTheme } from '../theme/ThemeContext';
import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import TablePermissionsListComponent from '../components/applications/TablePermissionsListComponent';
import ProgressBarDialog from '../components/common/ProgressBar';
import AlertDialog from '../components/common/AlertDialog';
import TablePermissionDialog from '../components/applications/TablePermissionDialog';

const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

const DEFAULT_PAGE_SIZE = 5;

export default function TablePermissionsListPage() {
    const { theme } = useTheme();
    // Adjust param names to your route; assuming userId + applicationId are available
    const { userId, id: applicationId } = useParams();
    const queryClient = useQueryClient();

    const [page, setPage] = React.useState(1);
    const [pageSize] = React.useState(DEFAULT_PAGE_SIZE);

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertTitle, setAlertTitle] = React.useState('');
    const [alertMessage, setAlertMessage] = React.useState('');
    const [alertType, setAlertType] = React.useState('info');
    const [permissionModalOpen, setPermissionModalOpen] = React.useState(false);
    const [editingPermission, setEditingPermission] = React.useState(null);

    const { data, isLoading, isError, error, isFetching } = useQuery({
        queryKey: ['table-permissions', userId, page, pageSize],
        queryFn: async ({ signal }) => {
            if (!userId) return { results: [], count: 0 };

            let url = `/arcee/user/table-permissions/list/${userId}/?page=${page}&page_size=${pageSize}`;
            if (applicationId) {
                url += `&application_id=${applicationId}`;
            }
            const resp = await apiHelper.apiCall(url, { method: 'GET', signal });

            if (!resp.ok) {
                let errBody;
                try {
                    errBody = await resp.json();
                } catch {
                    errBody = await resp.text().catch(() => null);
                }
                const e = new Error('Failed to fetch table permissions');
                e.info = errBody;
                throw e;
            }

            const body = await resp.json().catch(() => null);
            if (Array.isArray(body)) {
                return { results: body, count: body.length };
            }
            return {
                results: body?.results ?? body ?? [],
                count: body?.count ?? (Array.isArray(body) ? body.length : 0),
            };
        },
        enabled: !!userId,
        placeholderData: keepPreviousData,
        staleTime: 5000,
        cacheTime: 2 * 60 * 1000,
        retry: 1,
    });

    function formatErrorMessage(err) {
        if (!err) return 'An unknown error occurred.';
        if (err.info) {
            if (typeof err.info === 'string') return err.info;
            if (typeof err.info === 'object') {
                const parts = [];
                for (const [k, v] of Object.entries(err.info)) {
                    if (Array.isArray(v)) parts.push(`${k}: ${v.join(' ')}`);
                    else parts.push(`${k}: ${String(v)}`);
                }
                return parts.join('\n');
            }
        }
        return err.message || String(err);
    }

    React.useEffect(() => {
        if (isError) {
            setAlertTitle('Failed to load permissions');
            setAlertMessage(formatErrorMessage(error));
            setAlertType('error');
            setAlertOpen(true);
        }
    }, [isError, error]);

    const permissionsList = data?.results ?? [];
    const totalCount = data?.count ?? 0;
    const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

    // Handlers
    const handlePage = (p) => setPage(p);

    const handleCreate = () => {
        setEditingPermission(null);
        setPermissionModalOpen(true);
    };

    const handleEdit = (permission) => {
        setEditingPermission(permission);
        setPermissionModalOpen(true);
    };

    const handleAlertClose = () => {
        setAlertOpen(false);
    };

    return (
        <>
            {isLoading ? (
                <ProgressBarDialog
                    open={true}
                    text_message="Loading table permissions..."
                    cancellable={false}
                />
            ) : (
                <>
                    <TablePermissionsListComponent
                        permissions={permissionsList}
                        page={page}
                        pageCount={pageCount}
                        onCreate={handleCreate}
                        onEdit={handleEdit}
                        onPage={handlePage}
                    />

                    {isFetching && (
                        <ProgressBarDialog
                            open={true}
                            text_message="Refreshing table permissions..."
                            cancellable={false}
                        />
                    )}
                </>
            )}

            <AlertDialog
                open={alertOpen}
                title={alertTitle}
                message={alertMessage}
                type={alertType}
                okLabel="OK"
                showCancel={false}
                onOk={handleAlertClose}
                mini_mode={false}
            />

            <TablePermissionDialog
                open={permissionModalOpen}
                onClose={() => setPermissionModalOpen(false)}
                applicationId={applicationId}
                user={{ id: Number(userId) }}
                permission={editingPermission}
                onSaved={() => {
                    queryClient.invalidateQueries({
                        queryKey: ['table-permissions', userId],
                        exact: false,
                    });
                }}
            />
        </>
    );
}
